import { CheckCircle, Copy, Edit, ExternalLink, Plus } from 'lucide-react'
import { FaFacebook } from 'react-icons/fa'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  facebookAdsAdSetManagement,
  type FacebookAdsAdSetManagementResult,
} from 'sprites-types/workflow/tools/facebookAdsAdSetManagement'
import { ContextMenuWrapper, type ContextAction } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function FacebookAdsAdSetManagementDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(facebookAdsAdSetManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as FacebookAdsAdSetManagementResult

  const isCreate = 'created' in result && result.created

  const name = 'name' in result ? result.name : undefined
  const status = 'status' in result ? result.status : undefined
  const adAccountId = 'adAccountId' in result ? result.adAccountId : undefined

  // Extract metadata from inputs
  const inputs = nodeExecution.inputs.functionArgs as any
  const params = inputs?.action?.params || {}
  const campaignId = params.campaignId
  const dailyBudget = params.dailyBudget
  const lifetimeBudget = params.lifetimeBudget
  const optimizationGoal = params.optimizationGoal
  const billingEvent = params.billingEvent
  const targeting = params.targeting
  const conversionLocation = params.conversionLocation
  const bidStrategy = params.bidStrategy

  const formatBudget = (cents: number) => `$${(cents / 100).toFixed(2)}`

  const getTargetingSummary = () => {
    if (!targeting) return null
    const parts: string[] = []
    if (targeting.geo_locations?.countries) {
      parts.push(targeting.geo_locations.countries.join(', '))
    }
    if (targeting.age_min || targeting.age_max) {
      parts.push(`Ages ${targeting.age_min || 18}-${targeting.age_max || 65}`)
    }
    return parts.length > 0 ? parts.join(' • ') : null
  }

  // Strip 'act_' prefix if present for URL
  const accountIdForUrl = adAccountId?.replace(/^act_/, '')

  // Even though the search filter is called search by campaign id, for some reason this is how
  // we filter for adsets by id
  const filterSet = campaignId ? `SEARCH_BY_CAMPAIGN_ID-STRING\x1EEQUAL\x1E"${result.id}"` : null

  const adsManagerUrl = accountIdForUrl
    ? `https://business.facebook.com/adsmanager/manage/adsets?act=${accountIdForUrl}&selected_adset_ids=${result.id}${campaignId ? `&selected_campaign_ids=${campaignId}` : ''}${filterSet ? `&filter_set=${encodeURIComponent(filterSet)}` : ''}`
    : null

  const contextActions: ContextAction[] = [
    ...(adsManagerUrl
      ? [
          {
            id: 'open-ads-manager',
            label: 'Open in Ads Manager',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: () => window.open(adsManagerUrl, '_blank', 'noopener,noreferrer'),
          },
        ]
      : []),
    {
      id: 'copy-id',
      label: 'Copy Ad Set ID',
      icon: <Copy className="h-4 w-4" />,
      onClick: () => navigator.clipboard.writeText(result.id),
    },
    ...(adsManagerUrl
      ? [
          {
            id: 'copy-url',
            label: 'Copy Ads Manager URL',
            icon: <Copy className="h-4 w-4" />,
            onClick: () => navigator.clipboard.writeText(adsManagerUrl),
          },
        ]
      : []),
  ]

  return (
    <ContextMenuWrapper actions={contextActions}>
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-start gap-4 px-4 py-3">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">
              {isCreate ? (
                <Plus className="h-5 w-5 text-blue-600" />
              ) : (
                <Edit className="h-5 w-5 text-blue-600" />
              )}
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <FaFacebook className="h-4 w-4 text-[#0064e0]" />
              <div className="text-sm font-semibold text-gray-900">
                {isCreate ? 'Ad Set Created' : 'Ad Set Updated'}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2">
              {name && <div className="mb-1 text-sm font-medium text-gray-900">{name}</div>}
              <div className="space-y-1 text-xs">
                <div className="text-gray-500">Ad Set ID: {result.id}</div>
                {campaignId && <div className="text-gray-500">Campaign ID: {campaignId}</div>}
                {adAccountId && <div className="text-gray-500">Ad Account: {adAccountId}</div>}
                {/* Optimization Goal */}
                {optimizationGoal && (
                  <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-2">
                    <div className="mb-0.5 text-xs font-medium text-blue-600">
                      Optimization Goal:
                    </div>
                    <div className="text-sm font-semibold text-blue-900">
                      {optimizationGoal.replace(/_/g, ' ')}
                    </div>
                  </div>
                )}

                {/* Budget */}
                {(dailyBudget || lifetimeBudget) && (
                  <div className="mt-2 rounded-md border border-green-200 bg-green-50 p-2">
                    <div className="mb-0.5 text-xs font-medium text-green-600">Budget:</div>
                    <div className="text-sm font-semibold text-green-900">
                      {dailyBudget && <span>{formatBudget(dailyBudget)}/day</span>}
                      {dailyBudget && lifetimeBudget && <span className="text-green-700"> • </span>}
                      {lifetimeBudget && <span>{formatBudget(lifetimeBudget)} lifetime</span>}
                    </div>
                  </div>
                )}

                {/* Targeting */}
                {getTargetingSummary() && (
                  <div className="mt-2 rounded-md border border-purple-200 bg-purple-50 p-2">
                    <div className="mb-0.5 text-xs font-medium text-purple-600">Targeting:</div>
                    <div className="text-sm text-purple-900">{getTargetingSummary()}</div>
                  </div>
                )}

                {/* Conversion Location */}
                {conversionLocation && (
                  <div className="mt-2 text-xs text-gray-700">
                    <span className="text-gray-500">Conversion Location:</span>{' '}
                    <span className="font-medium">{conversionLocation.replace(/_/g, ' ')}</span>
                  </div>
                )}

                {/* Bid Strategy */}
                {bidStrategy && (
                  <div className="mt-1 text-xs text-gray-700">
                    <span className="text-gray-500">Bid Strategy:</span>{' '}
                    <span className="font-medium">{bidStrategy.replace(/_/g, ' ')}</span>
                  </div>
                )}

                {/* Billing Event */}
                {billingEvent && (
                  <div className="mt-1 text-xs text-gray-700">
                    <span className="text-gray-500">Billing Event:</span>{' '}
                    <span className="font-medium">{billingEvent}</span>
                  </div>
                )}
              </div>
              {status && (
                <div className="mt-2 flex items-center gap-2">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      status === 'ACTIVE'
                        ? 'bg-green-100 text-green-800'
                        : status === 'PAUSED'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {status}
                  </span>
                </div>
              )}
              {adsManagerUrl && (
                <div className="mt-2">
                  <a
                    href={adsManagerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <ExternalLink className="h-3 w-3" />
                    <span>View in Ads Manager</span>
                  </a>
                </div>
              )}
            </div>

            {isCreate && (
              <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                <CheckCircle className="h-3 w-3 text-green-600" />
                <span>Ad set is ready to use</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </ContextMenuWrapper>
  )
}

function downloadFacebookAdsAdSetManagement(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function facebookAdsAdSetManagementToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(facebookAdsAdSetManagement, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as FacebookAdsAdSetManagementResult
  const isCreate = 'created' in result && result.created

  const name = 'name' in result ? result.name : undefined
  const status = 'status' in result ? result.status : undefined
  const adAccountId = 'adAccountId' in result ? result.adAccountId : undefined

  // Extract metadata from inputs
  const inputs = nodeExecution.inputs.functionArgs as any
  const params = inputs?.action?.params || {}
  const campaignId = params.campaignId
  const dailyBudget = params.dailyBudget
  const lifetimeBudget = params.lifetimeBudget
  const conversionLocation = params.conversionLocation
  const billingEvent = params.billingEvent
  const targeting = params.targeting

  const formatBudget = (cents: number) => `$${(cents / 100).toFixed(2)}`

  const getTargetingSummary = () => {
    if (!targeting) return null
    const parts: string[] = []
    if (targeting.geo_locations?.countries) {
      parts.push(targeting.geo_locations.countries.join(', '))
    }
    if (targeting.age_min || targeting.age_max) {
      parts.push(`Ages ${targeting.age_min || 18}-${targeting.age_max || 65}`)
    }
    return parts.length > 0 ? parts.join(' • ') : null
  }

  let markdown = `## Facebook Ad Set ${isCreate ? 'Created' : 'Updated'}\n\n`
  if (name) markdown += `**Name:** ${name}\n`
  markdown += `**Ad Set ID:** ${result.id}\n`
  if (campaignId) markdown += `**Campaign ID:** ${campaignId}\n`
  if (adAccountId) markdown += `**Ad Account:** ${adAccountId}\n`
  if (conversionLocation) markdown += `**Optimization:** ${conversionLocation.replace(/_/g, ' ')}\n`
  if (billingEvent) markdown += `**Billing:** ${billingEvent}\n`
  if (dailyBudget || lifetimeBudget) {
    markdown += '**Budget:** '
    if (dailyBudget) markdown += `${formatBudget(dailyBudget)}/day`
    if (dailyBudget && lifetimeBudget) markdown += ' • '
    if (lifetimeBudget) markdown += `${formatBudget(lifetimeBudget)} lifetime`
    markdown += '\n'
  }
  const targetingSummary = getTargetingSummary()
  if (targetingSummary) markdown += `**Targeting:** ${targetingSummary}\n`
  if (status) markdown += `**Status:** ${status}\n`
  markdown += '\n---\n'

  return markdown
}

export const FacebookAdsAdSetManagementDisplay = createWorkflowToolComponent(
  FacebookAdsAdSetManagementDisplayComponent,
  downloadFacebookAdsAdSetManagement,
  facebookAdsAdSetManagementToMarkdown
)
